'use client'

/**
 * The account menu at the foot of the side navigation (#15618), modelled on
 * the classroom product's `account-menu.tsx` (SchoolAI/web-app,
 * `apps/web/src/app/_features/navigation/_components/account-menu.tsx`):
 * a sai-ui `DropdownMenu` opening upward from the account row, in four
 * sections —
 *
 *   1. YOU — Profile (with the role badge), Personal Settings. Dark mode
 *      lives in Personal Settings, not here (#15668).
 *   2. ORGANIZATION — the org switcher (2+ orgs only, the classroom product's
 *      `showSwitcher` rule) and Admin Settings (admins only).
 *   3. The org's configured user-menu sections (`account-menu-entries`).
 *   4. Log out (and the dev-only Component Tests link, as today).
 *
 * Org switching goes through `useSwitchOrg` — the EXISTING `switchOrg`
 * action, the same toast and the same `router.refresh()` as the header's
 * switcher. No new switching path.
 */

import type { OrgRole } from '@prisma/client-cadence'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuNestedTrigger,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenuButton,
} from '@sai/ui/components'
import Link from 'next/link'
import {
  useEffect,
  useState,
  type ComponentProps,
  type MouseEvent,
} from 'react'

import {
  Blocks,
  Building2,
  Check,
  ChevronRight,
  LogOut,
  Settings,
  Wrench,
} from '@/components/icons'
import { PersonAvatarFromPerson } from '@/components/person-avatar-from-person'
import { PersonRoleBadge } from '@/components/person-role-badge'
import { useSwitchOrg } from '@/hooks/use-switch-org'
import { signOutAndClearBrowserData } from '@/lib/auth/client-sign-out'
import { getDisplayName } from '@/lib/display-name'
import type { UserOrg } from '@/lib/get-user-orgs'
import type { UserMenuLayout } from '@/lib/org-nav/resolve'
import { isAdminRole } from '@/lib/role-utils'
import { cn } from '@/lib/utils'

import {
  AccountMenuSections,
  type AccountMenuEntryContext,
} from './account-menu-entries'
import { trackSideNavEvent } from './side-nav-tracking'
import { SIDEBAR_ROW_FOCUS_RING } from './sidebar-focus-ring'

/**
 * sai-ui's menu layer (#16990): above the sidebar (`z-nav`), the header stack
 * (`z-nav-secondary`), the recording bar and the mobile sheet (`z-modal`),
 * and under the feedback band. Set inline, which Radix copies onto its popper
 * wrapper; it was a literal 600 while sai-ui's `z-menu` emitted no CSS here.
 */
export const ACCOUNT_MENU_Z_INDEX = 'var(--z-menu)'

/**
 * The classroom product's `ProtectedDropdownMenuContent` window: the click
 * that opened a menu must not also select the item that appears under the
 * pointer — for the org list, that would switch orgs by accident.
 */
const OPEN_CLICK_GUARD_MS = 250

/** Extra indent per level of org hierarchy (ADR-0031 parent/child orgs). */
const DEPTH_INDENT_REM = 1
/** A depth-0 org row's left padding, matching the menu's item padding. */
const ORG_ROW_BASE_PADDING_REM = 0.5

function ProtectedDropdownMenuContent({
  style,
  ...props
}: ComponentProps<typeof DropdownMenuContent>) {
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setArmed(true), OPEN_CLICK_GUARD_MS)
    return () => window.clearTimeout(timer)
  }, [])
  return (
    <DropdownMenuContent
      {...props}
      // `auto`, never `undefined`, once armed: a modal Radix menu disables
      // pointer events on <body>, and the content's own `auto` is what
      // re-enables them on the menu.
      style={{
        ...style,
        zIndex: ACCOUNT_MENU_Z_INDEX,
        pointerEvents: armed ? 'auto' : 'none',
      }}
    />
  )
}

export interface AccountMenuUser {
  id: string
  name: string | null
  preferredName?: string | null
  email: string | null
  role?: OrgRole
}

export function AccountMenu({
  user,
  orgs,
  currentOrgId,
  layout,
  entryContext,
}: {
  user: AccountMenuUser
  orgs: UserOrg[]
  currentOrgId: string
  /** The resolved configurable sections (`resolveUserMenuLayout`). */
  layout: UserMenuLayout
  entryContext: AccountMenuEntryContext
}) {
  const [open, setOpen] = useState(false)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const { switchTo, isSwitching } = useSwitchOrg(orgs, currentOrgId)

  const currentOrg = orgs.find(org => org.orgId === currentOrgId)
  const showSwitcher = orgs.length >= 2
  const displayName =
    user.name || user.preferredName?.trim()
      ? getDisplayName({ preferredName: user.preferredName, name: user.name })
      : 'No Name'
  const avatarPerson = {
    id: user.id,
    name: user.name,
    preferredName: user.preferredName,
    email: user.email,
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) trackSideNavEvent({ event: 'account_menu_opened' })
  }

  /**
   * Close on ANY anchor click, as the classroom product does. Radix's
   * select-and-close is skipped when the item's `next/link` calls
   * `preventDefault` for a client-side navigation, so without this a
   * followed link would leave the menu open over the new page.
   */
  const closeOnAnchorClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as Element | null)?.closest('a')) setOpen(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <SidebarMenuButton
        asChild
        tooltip={displayName}
        // The classroom product's account row (no chevron, #15668).
        className={cn(
          'h-10.5 py-1 data-[state=open]:bg-fill-3 group-data-[collapsible=icon]:p-1!',
          SIDEBAR_ROW_FOCUS_RING,
        )}
      >
        <DropdownMenuTrigger data-testid="viewer-identity-menu">
          <PersonAvatarFromPerson
            person={avatarPerson}
            size="md"
            disableHover
          />
          <span className="flex min-w-0 flex-1 flex-col text-start leading-tight">
            <span className="truncate text-sm font-semibold text-text-primary">
              {displayName}
            </span>
            {currentOrg && (
              <span className="truncate text-xs text-text-primary">
                {currentOrg.orgName}
              </span>
            )}
          </span>
        </DropdownMenuTrigger>
      </SidebarMenuButton>

      <ProtectedDropdownMenuContent
        side="top"
        align="start"
        variant="inverse"
        className="w-82 max-h-[calc(100vh-4rem)] overflow-y-auto"
        data-testid="account-menu"
        onClick={closeOnAnchorClick}
      >
        {/* 1. You */}
        <DropdownMenuItem asChild>
          <Link href={`/people/${user.id}`} className="py-2">
            <PersonAvatarFromPerson
              person={avatarPerson}
              size="sm"
              disableHover
            />
            <span className="flex-1 truncate">Profile</span>
            {user.role && (
              <PersonRoleBadge role={user.role} size="sm" display="full" />
            )}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/settings">
            <Settings />
            <span className="flex-1">Personal Settings</span>
          </Link>
        </DropdownMenuItem>

        {/* 2. Organization */}
        {(showSwitcher || isAdminRole(user.role)) && <DropdownMenuSeparator />}
        {showSwitcher && (
          <DropdownMenu open={switcherOpen} onOpenChange={setSwitcherOpen}>
            <DropdownMenuNestedTrigger asChild>
              <DropdownMenuItem
                data-testid="account-menu-org-switcher"
                disabled={isSwitching}
                // Opening the org list must not close the account menu.
                onSelect={event => event.preventDefault()}
                // `DropdownMenuNestedTrigger` answers ArrowRight with a
                // synthetic `click()`, which the Radix trigger does not
                // handle (it opens on pointerdown, Enter and Space). A
                // keyboard click has `detail === 0`; a pointer click has
                // already toggled the list on pointerdown.
                onClick={event => {
                  if (event.detail === 0) setSwitcherOpen(true)
                }}
              >
                <Building2 />
                <span className="flex-1 truncate">
                  {currentOrg?.orgName ?? 'Organization'}
                </span>
                <ChevronRight />
              </DropdownMenuItem>
            </DropdownMenuNestedTrigger>
            <ProtectedDropdownMenuContent
              side="right"
              align="end"
              variant="inverse"
              className="w-72 max-h-[calc(100vh-4rem)] overflow-y-auto"
            >
              <DropdownMenuLabel className="text-xs text-text-secondary">
                Switch organization
              </DropdownMenuLabel>
              {orgs.map(org => {
                const isCurrent = org.orgId === currentOrgId
                return (
                  <DropdownMenuItem
                    key={org.orgId}
                    aria-current={isCurrent ? 'true' : undefined}
                    onSelect={() => {
                      setOpen(false)
                      void switchTo(org.orgId)
                    }}
                    style={{
                      paddingLeft: `${
                        ORG_ROW_BASE_PADDING_REM +
                        (org.depth ?? 0) * DEPTH_INDENT_REM
                      }rem`,
                    }}
                  >
                    <span className="flex size-4 shrink-0 items-center justify-center">
                      {isCurrent && <Check />}
                    </span>
                    <span
                      className={
                        isCurrent
                          ? 'flex-1 truncate font-medium'
                          : 'flex-1 truncate'
                      }
                    >
                      {org.orgName}
                    </span>
                  </DropdownMenuItem>
                )
              })}
            </ProtectedDropdownMenuContent>
          </DropdownMenu>
        )}
        {isAdminRole(user.role) && (
          <DropdownMenuItem asChild>
            <Link href="/admin">
              <Wrench />
              <span className="flex-1">Admin Settings</span>
            </Link>
          </DropdownMenuItem>
        )}

        {/* 3. The org's configured sections */}
        <AccountMenuSections layout={layout} ctx={entryContext} />

        {/* 4. Log out */}
        <DropdownMenuSeparator />
        {process.env.NODE_ENV === 'development' && (
          <DropdownMenuItem asChild>
            <Link href="/component-tests">
              <Blocks />
              <span className="flex-1">Component Tests</span>
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() =>
            void signOutAndClearBrowserData({ callbackUrl: '/auth/signin' })
          }
        >
          <LogOut />
          <span className="flex-1">Log out</span>
        </DropdownMenuItem>
      </ProtectedDropdownMenuContent>
    </DropdownMenu>
  )
}
